import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { Bundle } from '@adornis/buchungsheld-db/entities/Bundle.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { formatPrice, getBillData } from '@adornis/buchungsheld-db/global-helpers.js';
import type { PaymentPlugin } from '@adornis/buchungsheld-db/plugins/plugin-types.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import '@adornis/forms/x-checkbox.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { until } from 'lit/directives/until.js';
import { combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { BHRegistrationFlowSubPage } from './bh-page-registration-sub-page.js';

@customElement('bh-page-registration-payment')
export class BHPageRegistrationPayment extends BHRegistrationFlowSubPage {
  @state() private readonly institution = new RXController(
    this,
    this._registrationController$.pipe(switchMap(regController => regController?.institution$ ?? of(null))),
  );

  @state() private readonly paymentMethods = new RXController(
    this,
    this._registrationController$.pipe(switchMap(regController => regController?.availablePaymentMethods$ ?? of([]))),
  );

  @state() private readonly registrationBundles = new RXController(
    this,
    this._registrationController$.pipe(
      switchMap(regController => regController?.bundles$ ?? of([])),
      map(bundles => (bundles ?? []).filter(b => b.isActive)),
    ),
  );

  @state() private readonly user = new RXController(this, currentUser());
  @state() private readonly stammdaten = new RXController(
    this,
    combineLatest([this.institution.observable, this.user.observable]).pipe(
      filter(([institution, user]) => !!institution && !!user),
      switchMap(([institution, user]) =>
        UserStammdaten.getExistingStammdaten(user!._id, institution!._id, null)(UserStammdaten.allFields),
      ),
    ),
  );

  @state() private readonly products = new RXController(
    this,
    this._registrationController$.pipe(switchMap(regController => regController?.selectedProducts$ ?? of([]))),
  );

  @state() private readonly billData = new RXController(
    this,
    combineLatest([
      this.products.observable,
      this.registrationBundles.observable,
      this.user.observable.pipe(filter(isDefined)),
    ]).pipe(switchMap(([products, bundles, user]) => getBillData(products ?? [], bundles ?? [], user._id))),
  );

  private paymentMethod = '';

  override backAction = () => this._registrationController.previous();
  override nextAction = () => this._registrationController.pay(this.paymentMethod);
  override nextButtonText = ['REGISTRATION', 'book_now'];

  protected _renderProduct(
    curProd: Product,
    curUser: AdornisUser,
    payment: PaymentPlugin<string, string>,
    inBundle = false,
  ) {
    const price = (async () => {
      if (curProd instanceof Workshop && curProd.leftCapacity === 0) return 0; // waitingList products are paid when claiming a spot
      const price = await payment.getProductPrice(curProd._id, curUser._id);
      if (!price || !price.unitAmount) return null;
      return formatPrice(price.unitAmount);
    })();

    return [
      html`
        <tr-text ?bold=${!inBundle} ${css(inBundle ? { paddingLeft: this.spacing.xs } : {})}> ${curProd.name} </tr-text>
        <tr-text ${css(inBundle ? { textDecoration: 'line-through' } : {})}> ${until(price, '0,00 €')}</tr-text>
      `,
    ];
  }

  protected _renderBundle(curBundle: Bundle, curUser: AdornisUser, payment: PaymentPlugin<string, string>) {
    return html`
      <tr-text bold>${curBundle.name}</tr-text>
      <tr-text>${formatPrice(curBundle.getUnitAmount, '---')}</tr-text>

      ${repeat(curBundle.products ?? [], curBundleProd => this._renderProduct(curBundleProd, curUser, payment, true))}
    `;
  }

  override content() {
    const paymentMethods = this.paymentMethods.value;

    if (!this.institution.value || !paymentMethods || !this.billData.value)
      return html`<x-loading-state></x-loading-state>`;
    if (!this.paymentMethod) this.paymentMethod = paymentMethods[0] ?? '';

    this.nextButtonDisabled = paymentMethods.length === 0;
    if (paymentMethods.length === 0) return html`no paymentMethods found`;

    const stammdaten = this.stammdaten.value;
    const paymentPlugin = getPluginForScope('payment', this._registrationController.institutionID);
    const user = this.user.value;

    if (!user || !stammdaten) return html`<x-loading-state></x-loading-state>`;

    this._registrationController.bundles?.filter(bn => bn.isActive);

    return html`
      <x-h3> ${this._translation.translate('PAYMENT', 'payment')} </x-h3>
      <tr-text> ${this._translation.translate('PAYMENT', 'select_method')} </tr-text>

      <x-dropdown-selection
        .selectables=${this.paymentMethods.value}
        placeholder=${this._translation.translate('PAYMENT', 'method')}
        .value=${this.paymentMethod}
        .renderString=${item => this._translation.translate('PAYMENT', 'methods', item)}
        @value-picked=${(e: ValueEvent<any>) => {
          this.paymentMethod = e.detail.value;
        }}
      ></x-dropdown-selection>

      <!-- price preview -->
      <tr-flex
        crossaxis-end
        ${acss({
          padding: `0 ${this.spacing.lg}`,
          margin: '30px 0',

          'tr-grid': {
            gap: `${this.spacing.sm} ${this.spacing.md}`,

            'tr-text': { textWrap: 'nowrap' },
            '[total]': {
              display: 'contents',

              'tr-icon': {
                justifySelf: 'flex-end',
                alignSelf: 'center',
              },
              'tr-text': {
                borderTop: `2px solid ${this.colors.dark}`,
                padding: `${this.spacing.xs} 0`,
              },
            },
          },
        })}
      >
        <tr-grid vertical columns="min-content min-content">
          ${repeat(this.billData.value.booked, curBook => {
            return curBook instanceof Product
              ? this._renderProduct(curBook, user, paymentPlugin)
              : this._renderBundle(curBook.bundle, user, paymentPlugin);
          })}

          <tr-flex total>
            <tr-icon>point_of_sale</tr-icon>
            <tr-text>${formatPrice(this.billData.value.totalPrice, '0,00 €')}</tr-text>
          </tr-flex>
        </tr-grid>
      </tr-flex>
    `;
  }
}
